import { circle, type Circle } from './circle';
import { projectParameter } from './projection';
import { segment, type Segment } from './segment';
import type { Carrier } from './shape-ops';
import { carrier, liesOn, type Shape } from './shape';
import { dist, dot, len, lenSq, lerp, normalize, perp, scale, sub, add, type Vec2 } from './vec2';

/** The circle with `a` and `b` at the ends of a diameter; null when they coincide. */
export function circleOnDiameter(a: Vec2, b: Vec2): Circle | null {
  const diameter = dist(a, b);
  return diameter > 0 ? circle(lerp(a, b, 0.5), diameter / 2) : null;
}

/** The point of a line or circle nearest `p`; null from a circle's center. */
export function footOnCarrier(curve: Carrier, p: Vec2): Vec2 | null {
  if (curve.kind === 'line') {
    const direction = sub(curve.b, curve.a);
    const t = projectParameter(curve.a, direction, p);
    return t === null ? null : add(curve.a, scale(direction, t));
  }
  const offset = sub(p, curve.center);
  const length = len(offset);
  return length > 0 ? add(curve.center, scale(offset, curve.radius / length)) : null;
}

/**
 * Where a circle with a diameter ending at `toward` touches `shape`: the point of the
 * shape's line or circle nearest `toward`. Null from on the line or at the center, or where
 * the shape does not reach.
 */
export function touchToward(shape: Shape, toward: Vec2): Vec2 | null {
  const foot = footOnCarrier(carrier(shape), toward);
  return foot && dist(foot, toward) > 0 && liesOn(shape, foot) ? foot : null;
}

/** The signed distance from a line or circle to `p`, and the direction it grows fastest. */
function gapTo(curve: Carrier, p: Vec2): { signed: number; normal: Vec2 } | null {
  const normal =
    curve.kind === 'line'
      ? normalize(perp(sub(curve.b, curve.a)))
      : normalize(sub(p, curve.center));
  if (lenSq(normal) === 0) return null;
  const signed =
    curve.kind === 'line' ? dot(sub(p, curve.a), normal) : dist(p, curve.center) - curve.radius;
  return { signed, normal };
}

/** A shape to touch, and where on it it was picked. */
export interface Pick {
  readonly shape: Shape;
  readonly point: Vec2;
}

/**
 * The circle touching both picked shapes, on the side of each that the other pick is on,
 * so the picks choose the corner and `near` only sizes it: the center is the point nearest
 * `near` that is equally far from both, and that distance is the radius. Null where no
 * circle fits, or where a touch point falls off a line's or an arc's extent.
 */
export function circleTouching(a: Pick, b: Pick, near: Vec2): Circle | null {
  let best: Circle | null = null;
  for (const sideA of sidesFacing(a.shape, b.point)) {
    for (const sideB of sidesFacing(b.shape, a.point)) {
      const found = onSides(a.shape, b.shape, near, sideA, sideB);
      if (found && (!best || dist(found.center, near) < dist(best.center, near))) best = found;
    }
  }
  return best;
}

/** The side of `shape` that `p` is on, or both when `p` is on it. */
function sidesFacing(shape: Shape, p: Vec2): readonly number[] {
  const gap = gapTo(carrier(shape), p);
  return gap && Math.abs(gap.signed) > 1e-9 ? [Math.sign(gap.signed)] : [1, -1];
}

function onSides(a: Shape, b: Shape, near: Vec2, sideA: number, sideB: number): Circle | null {
  const curves = [carrier(a), carrier(b)] as const;
  let center = near;
  for (let step = 0; step < 16; step += 1) {
    const ga = gapTo(curves[0], center);
    const gb = gapTo(curves[1], center);
    if (!ga || !gb) return null;
    const excess = sideA * ga.signed - sideB * gb.signed;
    if (Math.abs(excess) < 1e-9) return touching(a, b, center, sideA * ga.signed);
    const gradient = sub(scale(ga.normal, sideA), scale(gb.normal, sideB));
    if (lenSq(gradient) < 1e-12) return null;
    center = sub(center, scale(gradient, excess / lenSq(gradient)));
  }
  return null;
}

function touching(a: Shape, b: Shape, center: Vec2, radius: number): Circle | null {
  if (radius < 1e-9) return null;
  return reachesFrom(a, center) && reachesFrom(b, center) ? circle(center, radius) : null;
}

/**
 * The circle touching both picked shapes and passing through `point`, with `point` on its far
 * side: of the circles that fit, the smallest, so it grows as the point moves away. Null
 * where none fits.
 */
export function circleThrough(a: Pick, b: Pick, point: Vec2): Circle | null {
  const rim = (center: Vec2) => {
    const offset = sub(center, point);
    return { value: len(offset), gradient: normalize(offset) };
  };
  const found = solveThird(a, b, [rim], point);
  return smallest(found);
}

/**
 * The circle touching all three picked shapes, each on the side the other picks are on;
 * where several fit, the one centered nearest `c`'s pick. Null where none fits.
 */
export function circleTouchingThree(a: Pick, b: Pick, c: Pick): Circle | null {
  const curve = carrier(c.shape);
  const constraints = sidesFacing(c.shape, a.point).map((side) => (center: Vec2) => {
    const gap = gapTo(curve, center);
    return gap && { value: side * gap.signed, gradient: scale(gap.normal, side) };
  });
  const found = solveThird(a, b, constraints, c.point).filter((candidate) =>
    reachesFrom(c.shape, candidate.center),
  );
  let best: Circle | null = null;
  for (const candidate of found) {
    if (!best || dist(candidate.center, c.point) < dist(best.center, c.point)) best = candidate;
  }
  return best;
}

/** What a third condition says the radius must be at a center, and how it changes there. */
type Third = (center: Vec2) => { value: number; gradient: Vec2 } | null;

/**
 * Every circle touching both picks whose radius also meets one of `thirds`, found by Newton
 * steps on the center from starts around `near`.
 */
function solveThird(a: Pick, b: Pick, thirds: readonly Third[], near: Vec2): Circle[] {
  const curves = [carrier(a.shape), carrier(b.shape)] as const;
  const footA = footOnCarrier(curves[0], near) ?? near;
  const footB = footOnCarrier(curves[1], near) ?? near;
  const starts = [near, lerp(near, footA, 0.5), lerp(near, footB, 0.5), lerp(footA, footB, 0.5)];
  const found: Circle[] = [];
  for (const sideA of sidesFacing(a.shape, b.point)) {
    for (const sideB of sidesFacing(b.shape, a.point)) {
      for (const third of thirds) {
        for (const start of starts) {
          const solved = newton(curves, sideA, sideB, third, start);
          const fits = solved && touching(a.shape, b.shape, solved.center, solved.radius);
          if (fits && !found.some((other) => dist(other.center, fits.center) < 1e-6)) {
            found.push(fits);
          }
        }
      }
    }
  }
  return found;
}

function newton(
  curves: readonly [Carrier, Carrier],
  sideA: number,
  sideB: number,
  third: Third,
  start: Vec2,
): Circle | null {
  let center = start;
  for (let step = 0; step < 40; step += 1) {
    const ga = gapTo(curves[0], center);
    const gb = gapTo(curves[1], center);
    const gc = third(center);
    if (!ga || !gb || !gc) return null;
    const radius = sideA * ga.signed;
    const f1 = radius - sideB * gb.signed;
    const f2 = radius - gc.value;
    if (Math.abs(f1) < 1e-9 && Math.abs(f2) < 1e-9) return circle(center, radius);
    const j1 = sub(scale(ga.normal, sideA), scale(gb.normal, sideB));
    const j2 = sub(scale(ga.normal, sideA), gc.gradient);
    const det = j1.x * j2.y - j1.y * j2.x;
    if (Math.abs(det) < 1e-12) return null;
    center = sub(center, {
      x: (f1 * j2.y - f2 * j1.y) / det,
      y: (j1.x * f2 - j2.x * f1) / det,
    });
  }
  return null;
}

function reachesFrom(shape: Shape, center: Vec2): boolean {
  const foot = footOnCarrier(carrier(shape), center);
  return foot !== null && liesOn(shape, foot, 1e-6);
}

function smallest(circles: readonly Circle[]): Circle | null {
  let best: Circle | null = null;
  for (const candidate of circles) if (!best || candidate.radius < best.radius) best = candidate;
  return best;
}

/**
 * Every line touching both circles or arcs, from touch point on `a` to touch point on `b`:
 * the outer ones, then the inner ones. On an arc, a touch point must fall on the arc. Empty
 * when one circle is inside the other, or no touch point falls on the arcs.
 */
export function linesTouching(a: Shape, b: Shape): Segment[] {
  const [ca, cb] = [carrier(a), carrier(b)];
  if (ca.kind !== 'circle' || cb.kind !== 'circle') return [];
  const between = sub(cb.center, ca.center);
  const apart = len(between);
  const found: Segment[] = [];
  for (const sign of [1, -1]) {
    const q = (sign * cb.radius - ca.radius) / apart;
    if (!(apart > 0) || Math.abs(q) > 1 + 1e-12) continue;
    const across = Math.sqrt(Math.max(0, 1 - q * q));
    const along = normalize(between);
    for (const turn of across === 0 ? [1] : [1, -1]) {
      const normal = add(scale(along, q), scale(perp(along), turn * across));
      const ta = sub(ca.center, scale(normal, ca.radius));
      const tb = sub(cb.center, scale(normal, sign * cb.radius));
      if (dist(ta, tb) > 1e-9 && liesOn(a, ta) && liesOn(b, tb)) found.push(segment(ta, tb));
    }
  }
  return found;
}
